import * as THREE from 'three/webgpu';
import {BehaviorSubject, type Observable} from 'rxjs';
import type {PointSelectionCandidate} from '../../reference/reference-point/ReferencePointController';
import type {SketchGroup} from '../../mesh/sketch/SketchGroup';
import type {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import {isSketchPointObject} from '../../mesh/sketch';
import type {SketchRuntimeHistorySession, Viewer} from '../../viewer/Viewer';
import {
    SketchEntitiesTransformGeometryService,
    SketchEntitiesTransformSelectionService,
} from '../entities-transform';
import type {
    SketchCircularPatternCenterItem,
    SketchCircularPatternControllerSnapshot,
    SketchCircularPatternInstanceRecord,
    SketchCircularPatternParameters,
    SketchCircularPatternSelectionRole,
} from '../../../types/sketch/circular-pattern/SketchCircularPatternTypes';
import type {
    SketchCircularPatternRelationInstanceGroup,
} from '../../../types/sketch/circular-pattern/SketchCircularPatternRelationBuilder';
import {SketchCircularDistributionCalculator} from './SketchCircularDistributionCalculator';
import {SketchCircularPatternObject} from './SketchCircularPatternObject';
import {SketchCircularPatternPreviewGuide} from './SketchCircularPatternPreviewGuide';
import {SketchCircularPatternRadiusAnchorResolver} from './SketchCircularPatternRadiusAnchorResolver';
import {SketchCircularPatternRelationBuilder} from './SketchCircularPatternRelationBuilder';
import {SketchCircularPatternSkipInstancePreview} from './SketchCircularPatternSkipInstancePreview';

/** 圆周阵列最小实例数。 */
const MINIMUM_PATTERN_INSTANCE_COUNT = 2;

/** 圆周阵列几何有效性容差。 */
const PATTERN_GEOMETRY_EPSILON = 1e-10;

/** 四阶恒等变换矩阵元素，用于判断源实例是否需要额外预览。 */
const IDENTITY_TRANSFORM_ELEMENTS: readonly number[] = [
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1,
];

/**
 * 圆周草图阵列控制器。
 *
 * 职责：
 * 1. 管理源实体、阵列中心、参数和跳过实例选择；
 * 2. 生成可回滚预览，并在确认时原子创建普通草图实体副本；
 * 3. 创建独立的阵列定义对象，维护源实体与实例稳定身份；
 * 4. 将确认结果接入活动草图运行时历史。
 */
export class SketchCircularPatternController {
    /** 圆周分布计算器。 */
    private readonly distributionCalculator = new SketchCircularDistributionCalculator();

    /** 草图实体选择域服务。 */
    private readonly selectionService = new SketchEntitiesTransformSelectionService();

    /** 草图实体克隆与矩阵变换服务。 */
    private readonly geometryService = new SketchEntitiesTransformGeometryService();

    /** 通用圆周阵列聚合关系构建器。 */
    private readonly relationBuilder = new SketchCircularPatternRelationBuilder();

    /** 圆周阵列半径语义锚点解析器。 */
    private readonly radiusAnchorResolver = new SketchCircularPatternRadiusAnchorResolver();

    /** 控制器快照流。 */
    private readonly snapshotSubject = new BehaviorSubject<SketchCircularPatternControllerSnapshot>(
        this.createInitialSnapshot(),
    );

    /** 当前命令是否激活。 */
    private active = false;

    /** 当前选择角色。 */
    private activeSelectionRole: SketchCircularPatternSelectionRole = 'entities';

    /** 当前源实体。 */
    private selectedEntities: THREE.Object3D[] = [];

    /** 当前中心点显示项。 */
    private centerItem: SketchCircularPatternCenterItem | null = null;

    /** 当前阵列参数。 */
    private parameters: SketchCircularPatternParameters = this.createDefaultParameters();

    /** 源实体开始阵列时的半径锚点世界坐标。 */
    private sourceAnchorWorld: THREE.Vector3 | null = null;

    /** 源实体当前用于定义半径的真实控制点。 */
    private sourceAnchorPoint: SketchPointObject | null = null;

    /** 源实体开始阵列时的极角。 */
    private sourceStartAngleRad = 0;

    /** 当前预览对象。 */
    private previewObjects: THREE.Object3D[] = [];

    /** 当前圆周路径、半径线和传播方向辅助对象。 */
    private previewGuide: SketchCircularPatternPreviewGuide | null = null;

    /** 当前跳过实例黄色候选与红色锚点预览。 */
    private skipInstancePreview: SketchCircularPatternSkipInstancePreview | null = null;

    /** 当前确认历史会话。 */
    private historySession: SketchRuntimeHistorySession | null = null;

    /**
     * 创建圆周草图阵列控制器。
     *
     * @param viewer Viewer 实例。
     */
    public constructor(private readonly viewer: Viewer) {}

    /**
     * 启动圆周草图阵列命令。
     *
     * @param initialEntities 启动前已经选中的草图实体。
     */
    public start(initialEntities: readonly THREE.Object3D[] = []): void {
        this.rollbackPreview();
        this.active = true;
        this.activeSelectionRole = 'entities';
        this.selectedEntities = this.resolveEntities(initialEntities);
        this.parameters = this.createDefaultParameters();
        this.initializeCenterAndSourceGeometry();
        this.refreshPreview();
        this.notifySnapshot();
    }

    /**
     * 获取当前控制器快照。
     *
     * @returns 控制器快照。
     */
    public getSnapshot(): SketchCircularPatternControllerSnapshot {
        return this.createSnapshot();
    }

    /**
     * 监听控制器快照。
     *
     * @returns 快照可观察流。
     */
    public observeSnapshot(): Observable<SketchCircularPatternControllerSnapshot> {
        return this.snapshotSubject.asObservable();
    }

    /**
     * 设置当前选择角色。
     *
     * @param role 选择角色。
     */
    public setActiveSelectionRole(role: SketchCircularPatternSelectionRole): void {
        if (!this.active) {
            return;
        }
        const previousRole = this.activeSelectionRole;
        this.activeSelectionRole = role;
        if (previousRole === 'skipped-instances' || role === 'skipped-instances') {
            this.refreshPreview();
        }
        this.notifySnapshot();
    }

    /**
     * 获取当前全部可跳过实例选择点。
     *
     * @returns 红色或白色选择点列表。
     */
    public getSkipInstanceMarkerObjects(): THREE.Sprite[] {
        return this.skipInstancePreview?.getMarkerObjects() ?? [];
    }

    /**
     * 处理视口实体点选。
     *
     * @param object 命中的视口对象。
     */
    public handleEntityPick(object: THREE.Object3D): void {
        if (!this.active) {
            return;
        }

        if (this.activeSelectionRole === 'skipped-instances') {
            this.toggleSkippedPreviewInstance(object);
            return;
        }

        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup) {
            return;
        }
        const entity = this.selectionService.resolveTransformEntity(object, sketchGroup);
        if (!entity) {
            return;
        }

        const existingIndex = this.selectedEntities.findIndex((candidate) => candidate.uuid === entity.uuid);
        if (existingIndex >= 0) {
            this.selectedEntities.splice(existingIndex, 1);
        } else {
            this.selectedEntities.push(entity);
        }
        this.onSourceSelectionChanged();
    }

    /**
     * 处理视口框选源实体。
     *
     * @param objects 框选命中的对象。
     */
    public handleEntityBoxSelection(objects: readonly THREE.Object3D[]): void {
        if (!this.active || this.activeSelectionRole !== 'entities') {
            return;
        }
        const resolvedEntities = this.resolveEntities(objects);
        const entityMap = new Map(this.selectedEntities.map((entity) => [entity.uuid, entity]));
        resolvedEntities.forEach((entity) => entityMap.set(entity.uuid, entity));
        this.selectedEntities = [...entityMap.values()];
        this.onSourceSelectionChanged();
    }

    /**
     * 删除一个已选源实体。
     *
     * @param objectId 草图对象运行时 ID。
     */
    public removeSelectedEntity(objectId: string): void {
        const nextEntities = this.selectedEntities.filter((entity) => entity.uuid !== objectId);
        if (nextEntities.length === this.selectedEntities.length) {
            return;
        }
        this.selectedEntities = nextEntities;
        this.onSourceSelectionChanged();
    }

    /**
     * 清空所有已选源实体。
     */
    public clearSelectedEntities(): void {
        if (this.selectedEntities.length === 0) {
            return;
        }
        this.selectedEntities = [];
        this.onSourceSelectionChanged();
    }

    /**
     * 处理阵列中心候选。
     *
     * 兼容公共参考点候选协议，但只接受能解析回当前活动草图
     * `SketchPointObject` 的稳定身份；自由平面点和其他参考几何一律拒绝。
     *
     * @param candidate 参考点候选。
     * @returns 是否成功消费候选。
     */
    public handleReferencePointSelection(candidate: PointSelectionCandidate): boolean {
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup) {
            return false;
        }
        const pointObject = sketchGroup.getObjectByProperty('uuid', candidate.entityId)
            ?? sketchGroup.getObjectByProperty('persistentId', candidate.entityId);
        return isSketchPointObject(pointObject)
            ? this.handleCenterPointSelection(pointObject)
            : false;
    }

    /**
     * 处理活动草图内的阵列中心点选择。
     *
     * 设计说明：
     * 1. `center2d` 始终保存有效中心坐标，而 `centerItem` 只表达真实点引用；
     * 2. 默认原点和数值 X/Y 属于自由坐标，不得伪造为点选择项；
     * 3. 只接受当前活动草图子树中的 `SketchPointObject`，防止跨草图引用。
     *
     * @param point 视口中明确命中的控制点或草图点。
     * @returns 成功设置阵列中心时返回 true。
     */
    public handleCenterPointSelection(point: SketchPointObject): boolean {
        if (!this.active || this.activeSelectionRole !== 'center') {
            return false;
        }
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup || sketchGroup.getObjectByProperty('uuid', point.uuid) !== point) {
            return false;
        }

        const pointPosition = point.getPosition();
        const localPoint = sketchGroup.referencePlane.worldToLocal(pointPosition);
        this.centerItem = {
            id: point.uuid,
            label: point.name.trim() || (point.isControlPoint ? '控制点' : '草图点'),
            position: pointPosition,
        };
        this.parameters = {
            ...this.parameters,
            center2d: [localPoint.x, localPoint.y],
        };
        this.recalculateRadiusAndStartAngleFromSource();
        this.sourceStartAngleRad = THREE.MathUtils.degToRad(this.parameters.startAngleDeg);
        this.refreshPreview();
        this.notifySnapshot();
        return true;
    }

    /**
     * 更新阵列参数。
     *
     * @param patch 参数补丁。
     */
    public updateParameters(patch: Partial<SketchCircularPatternParameters>): void {
        if (!this.active) {
            return;
        }
        const normalizedParameters = this.normalizeParameters({...this.parameters, ...patch});
        this.parameters = normalizedParameters;
        if (patch.center2d) {
            // 数值 X/Y 只定义自由中心坐标，不代表场景中存在可持久的点引用。
            this.centerItem = null;
            // 中心变化必须重新裁决最近语义控制点，并把该点的真实极角作为后续变换基线。
            this.recalculateRadiusAndStartAngleFromSource();
            this.sourceStartAngleRad = THREE.MathUtils.degToRad(this.parameters.startAngleDeg);
            this.parameters = {
                ...this.parameters,
                // 同一批更新显式携带半径或初始角时保留调用方输入，其余字段使用新中心推导值。
                ...(patch.radius !== undefined ? {radius: normalizedParameters.radius} : {}),
                ...(patch.startAngleDeg !== undefined
                    ? {startAngleDeg: normalizedParameters.startAngleDeg}
                    : {}),
            };
        }
        this.refreshPreview();
        this.notifySnapshot();
    }

    /**
     * 从面板移除一个已跳过实例，使其重新进入阵列。
     *
     * @param instanceIndex 稳定实例索引。
     */
    public restoreSkippedInstance(instanceIndex: number): void {
        const skippedInstanceIndexes = this.parameters.skippedInstanceIndexes.filter((index) => index !== instanceIndex);
        if (skippedInstanceIndexes.length === this.parameters.skippedInstanceIndexes.length) {
            return;
        }
        this.updateParameters({skippedInstanceIndexes});
    }

    /**
     * 确认创建圆周草图阵列。
     *
     * @returns 创建成功时返回 true。
     */
    public confirm(): boolean {
        if (!this.canConfirm()) {
            return false;
        }

        const sketchGroup = this.resolveActiveSketchGroup();
        const sourceAnchorWorld = this.sourceAnchorWorld?.clone();
        if (!sketchGroup || !sourceAnchorWorld) {
            return false;
        }

        this.rollbackPreview();
        this.historySession = this.viewer.beginActiveSketchRuntimeHistorySession({
            label: '创建圆周草图阵列',
            commandType: 'sketch.runtime.circular-pattern.create',
        });

        try {
            const distributionItems = this.calculateDistributionItems();
            const sourcePersistentIds = this.resolveSourcePersistentIds();
            const instanceRecords: SketchCircularPatternInstanceRecord[] = [];
            const relationInstanceGroups: SketchCircularPatternRelationInstanceGroup[] = [];
            const sourceItem = distributionItems.find((item) => item.isSource);
            if (!sourceItem) {
                throw new Error('圆周草图阵列缺少源实例。');
            }

            // 源实例属于既有草图主状态，必须经过统一参数系统与当前关系、尺寸共同求解。
            this.applySourceInstanceTransform(sourceItem.angleRad, sourceItem.rotationRad, sourceAnchorWorld);
            const committedSourceAnchorWorld = this.sourceAnchorPoint?.getPosition() ?? null;
            if (!committedSourceAnchorWorld) {
                throw new Error('圆周草图阵列无法解析确认后的源实例半径锚点。');
            }

            // 派生实例从已经求解的源几何复制，避免约束让源实体发生合法调整后副本仍使用旧形状。
            distributionItems.filter((item) => !item.isSource).forEach((item) => {
                const persistentIdCloneMap = new Map<string, string>();
                const selectedCloneMap = new Map<string, THREE.Object3D>();
                const clones = this.geometryService.cloneEntities(
                    this.selectedEntities,
                    sketchGroup,
                    selectedCloneMap,
                    persistentIdCloneMap,
                );
                const matrix = this.createDerivedInstanceTransformMatrix(
                    item.angleRad,
                    item.rotationRad,
                    committedSourceAnchorWorld,
                );
                this.geometryService.applyMatrixToEntitiesWithoutConstraintSolve(clones, matrix);
                const instanceRoots = this.selectedEntities
                    .map((entity) => selectedCloneMap.get(entity.uuid))
                    .filter((entity): entity is THREE.Object3D => Boolean(entity));
                instanceRecords.push({
                    instanceIndex: item.instanceIndex,
                    entityPersistentIds: sourcePersistentIds
                        .map((persistentId) => persistentIdCloneMap.get(persistentId))
                        .filter((persistentId): persistentId is string => typeof persistentId === 'string'),
                });
                relationInstanceGroups.push({
                    instanceIndex: item.instanceIndex,
                    rotationRad: item.rotationRad,
                    targets: this.geometryService.collectCircularPatternConstraintTargets(instanceRoots),
                });
            });
            instanceRecords.unshift({
                instanceIndex: 0,
                entityPersistentIds: sourcePersistentIds,
            });

            const patternObject = new SketchCircularPatternObject({
                sourceEntityPersistentIds: sourcePersistentIds,
                parameters: this.parameters,
                instances: instanceRecords,
            });
            sketchGroup.add(patternObject);
            const centerCandidate = this.centerItem
                ? sketchGroup.getObjectByProperty('uuid', this.centerItem.id)
                    ?? sketchGroup.getObjectByProperty('persistentId', this.centerItem.id)
                : null;
            const centerPoint = centerCandidate && isSketchPointObject(centerCandidate)
                ? centerCandidate
                : undefined;
            const seedTargets = this.geometryService.collectCircularPatternConstraintTargets(this.selectedEntities);
            const relationBuildResult = this.relationBuilder.build({
                patternObject,
                center2d: [this.parameters.center2d[0], this.parameters.center2d[1]],
                ...(centerPoint ? {centerPoint} : {}),
                seedTargets,
                instanceGroups: relationInstanceGroups,
                rotateInstances: this.parameters.rotateInstances,
            });
            const relationSynchronized = this.viewer.sketchRelationCreationService
                ?.replaceRelationsWithinActiveMutation({
                    relationsToRemove: [],
                    creations: [],
                    attachments: [relationBuildResult.relationAttachment],
                    seedObjects: [
                        ...(centerPoint ? [centerPoint] : []),
                        ...seedTargets,
                    ],
                }) ?? false;
            if (!relationSynchronized) {
                throw new Error('圆周草图阵列聚合关系创建或统一参数求解失败。');
            }
            sketchGroup.markDefinitionDirty({
                reason: 'sketch.runtime.circular-pattern.create',
                sourceObjectId: patternObject.uuid,
            });
            sketchGroup.commitDefinitionChanges({
                reason: 'sketch.runtime.circular-pattern.create',
                sourceObjectId: patternObject.uuid,
            });
            this.commitHistorySession();
        } catch (error) {
            this.cancelHistorySession();
            throw error;
        }

        this.active = false;
        this.notifySnapshot();
        this.viewer.clearSketchCircularPatternMode();
        return true;
    }

    /**
     * 取消圆周草图阵列命令。
     */
    public cancel(): void {
        this.rollbackPreview();
        this.cancelHistorySession();
        this.active = false;
        this.notifySnapshot();
    }

    /**
     * 释放控制器资源。
     */
    public dispose(): void {
        this.cancel();
        this.snapshotSubject.complete();
    }

    /**
     * 源实体选择发生变化后的统一处理。
     */
    private onSourceSelectionChanged(): void {
        // 增删多选实体时保留用户已经指定的阵列中心，只重新裁决全局最近锚点。
        this.recalculateRadiusAndStartAngleFromSource();
        this.sourceStartAngleRad = THREE.MathUtils.degToRad(this.parameters.startAngleDeg);
        this.refreshPreview();
        this.notifySnapshot();
    }

    /**
     * 初始化默认中心、源半径锚点及极角参数。
     */
    private initializeCenterAndSourceGeometry(): void {
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup) {
            this.centerItem = null;
            this.sourceAnchorWorld = null;
            this.sourceAnchorPoint = null;
            return;
        }

        // 默认使用草图局部 (0, 0)，但未明确点选时不向 PropertyManager 暴露“原点”伪选择项。
        this.centerItem = null;
        this.parameters = {...this.parameters, center2d: [0, 0]};
        this.recalculateRadiusAndStartAngleFromSource();
        this.sourceStartAngleRad = THREE.MathUtils.degToRad(this.parameters.startAngleDeg);
    }

    /**
     * 根据当前源几何与中心重新计算半径和起始角。
     */
    private recalculateRadiusAndStartAngleFromSource(): void {
        const sketchGroup = this.resolveActiveSketchGroup();
        const sourceAnchorWorld = this.resolveSourceAnchorWorld();
        if (!sketchGroup || !sourceAnchorWorld) {
            this.sourceAnchorWorld = null;
            return;
        }
        this.sourceAnchorWorld = sourceAnchorWorld;
        const sourceLocal = sketchGroup.referencePlane.worldToLocal(sourceAnchorWorld);
        const dx = sourceLocal.x - this.parameters.center2d[0];
        const dy = sourceLocal.y - this.parameters.center2d[1];
        const radius = Math.hypot(dx, dy);
        const startAngleDeg = THREE.MathUtils.radToDeg(Math.atan2(dy, dx));
        this.parameters = this.normalizeParameters({
            ...this.parameters,
            radius: Math.max(radius, PATTERN_GEOMETRY_EPSILON),
            startAngleDeg,
        });
    }

    /**
     * 解析源实体用于定义阵列半径的语义锚点。
     *
     * 直线和圆弧只比较两个端点，圆只使用圆心；多实体选择从全部候选控制点中取距离阵列中心最近者。
     * 解析器返回真实控制点身份，确认阶段可在源实体变换后继续读取同一点，禁止重新裁决导致锚点跳变。
     *
     * @returns 半径锚点世界坐标；没有可用候选时返回 null。
     */
    private resolveSourceAnchorWorld(): THREE.Vector3 | null {
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup) {
            this.sourceAnchorPoint = null;
            return null;
        }
        const patternCenterWorld = sketchGroup.referencePlane.localToWorld(
            this.parameters.center2d[0],
            this.parameters.center2d[1],
        );
        this.sourceAnchorPoint = this.radiusAnchorResolver.resolve(
            this.selectedEntities,
            patternCenterWorld,
        );
        return this.sourceAnchorPoint?.getPosition() ?? null;
    }

    /**
     * 刷新圆周阵列预览。
     */
    private refreshPreview(): void {
        this.rollbackInstancePreview();
        if (!this.active || !this.canPreview()) {
            this.clearPreviewGuide();
            this.clearSkipInstancePreview();
        this.viewer.requestRender();
            return;
        }
        const sketchGroup = this.resolveActiveSketchGroup();
        const sourceAnchorWorld = this.sourceAnchorWorld;
        if (!sketchGroup || !sourceAnchorWorld) {
            this.clearPreviewGuide();
            this.clearSkipInstancePreview();
        this.viewer.requestRender();
            return;
        }

        // 辅助圆弧与实例预览必须由同一组参数驱动，避免面板输入后两类视觉出现一帧错位。
        this.syncPreviewGuide(sketchGroup);
        const isSkipSelectionActive = this.activeSelectionRole === 'skipped-instances';
        if (!isSkipSelectionActive) {
            this.clearSkipInstancePreview();
        }

        this.calculateDistributionItems().forEach((item) => {
            const matrix = this.createInstanceTransformMatrix(item.angleRad, item.rotationRad, sourceAnchorWorld);
            const isIdentitySourceTransform = item.isSource && matrix.elements.every((value, index) => (
                Math.abs(value - IDENTITY_TRANSFORM_ELEMENTS[index]!) <= PATTERN_GEOMETRY_EPSILON
            ));
            /**
             * 默认源实例直接由真实源实体承担，既不隐藏也不创建重叠副本。
             * 只有半径或初始角使源实例发生实际变换时，才额外创建候选源实例预览；
             * 真实源实体仍保持可见，明确表达“当前几何”和“候选结果”的差异。
             */
            if (isIdentitySourceTransform) {
                return;
            }

            const clones = this.geometryService.cloneEntities(this.selectedEntities, sketchGroup);
            this.geometryService.applyMatrixToEntitiesWithoutConstraintSolve(clones, matrix);
            if (isSkipSelectionActive && !item.isSource) {
                this.ensureSkipInstancePreview().applySelectableInstanceStyle(clones);
            }
            clones.forEach((clone) => {
                this.previewObjects.push(clone);
            });
        });
        if (isSkipSelectionActive) {
            this.syncSkipInstancePreview(sketchGroup);
        }
        this.viewer.syncSketchEditingVisualLayering({previewRoots: this.previewObjects});
        this.viewer.requestRender();
    }

    /**
     * 回滚并移除当前预览。
     */
    private rollbackPreview(): void {
        this.rollbackInstancePreview();
        this.clearPreviewGuide();
        this.clearSkipInstancePreview();
        this.viewer.requestRender();
    }

    /**
     * 仅移除当前阵列实例候选预览。
     *
     * 参数连续编辑期间保留并原位更新辅助对象，避免重复创建 GPU 资源。
     */
    private rollbackInstancePreview(): void {
        this.previewObjects.forEach((object) => object.parent?.remove(object));
        this.previewObjects = [];
        // 预览子树已离场时立即清理 Viewer 的命令预览索引，避免保留无效运行时引用。
        this.viewer.syncSketchEditingVisualLayering({previewRoots: []});
    }

    /**
     * 同步圆周阵列命令态辅助圆弧、径向线与方向箭头。
     *
     * @param sketchGroup 当前活动草图组。
     */
    private syncPreviewGuide(sketchGroup: SketchGroup): void {
        const referencePlane = sketchGroup.referencePlane;
        const guide = this.ensurePreviewGuide();
        guide.updateFrame({
            centerWorld: referencePlane.localToWorld(
                this.parameters.center2d[0],
                this.parameters.center2d[1],
            ),
            xAxisWorld: referencePlane.xAxis,
            yAxisWorld: referencePlane.yAxis,
            radius: this.parameters.radius,
            startAngleRad: THREE.MathUtils.degToRad(this.parameters.startAngleDeg),
            totalAngleRad: this.resolvePreviewSweepAngleRad(),
            directionArrowOffsetAngleRad: this.resolvePreviewDirectionArrowOffsetAngleRad(),
            direction: this.parameters.reverseDirection ? 'clockwise' : 'counterclockwise',
        });

        // 首次参数帧已经创建精确顶点缓冲后才允许入场，禁止空线对象触发场景 BVH 初始化。
        if (!guide.parent) {
            this.viewer.addObject(guide);
        }
    }

    /**
     * 解析预览辅助圆弧的实际传播角度。
     *
     * 等间距模式由总角度控制；非等间距模式由相邻角间距与实例数共同决定。
     * 该规则与 `SketchCircularDistributionCalculator` 的实例角度保持一致，避免辅助圆弧终点与正式实例脱节。
     *
     * @returns 无方向符号的预览扫掠角，单位为弧度。
     */
    private resolvePreviewSweepAngleRad(): number {
        if (this.parameters.equalSpacing) {
            return THREE.MathUtils.degToRad(this.parameters.totalAngleDeg);
        }
        const distributionItems = this.calculateDistributionItems([]);
        const finalItem = distributionItems[distributionItems.length - 1];
        return Math.min(Math.PI * 2, Math.abs(finalItem?.rotationRad ?? 0));
    }

    /**
     * 从统一分布结果解析方向箭头所在的第一个实例角间距。
     *
     * @returns 种子到稳定索引 1 的无符号角间距，单位为弧度。
     */
    private resolvePreviewDirectionArrowOffsetAngleRad(): number {
        const firstDerivedItem = this.calculateDistributionItems([])
            .find((item) => item.instanceIndex === 1);
        return Math.abs(firstDerivedItem?.rotationRad ?? 0);
    }

    /**
     * 确保圆周阵列预览辅助对象已经创建。
     *
     * 对象在这里仅绑定视口上下文，必须由 `syncPreviewGuide()` 写入首个有效参数帧后再挂载到 Viewer。
     *
     * @returns 可复用的辅助对象。
     */
    private ensurePreviewGuide(): SketchCircularPatternPreviewGuide {
        if (this.previewGuide) {
            return this.previewGuide;
        }

        const guide = new SketchCircularPatternPreviewGuide();
        guide.bindScreenSizeContext(
            () => this.viewer.getCamera(),
            () => this.viewer.getContainer(),
        );
        this.previewGuide = guide;
        return guide;
    }

    /**
     * 移除并释放圆周阵列预览辅助对象。
     */
    private clearPreviewGuide(): void {
        const guide = this.previewGuide;
        if (!guide) {
            return;
        }
        guide.parent?.remove(guide);
        guide.dispose();
        this.previewGuide = null;
    }

    /**
     * 同步全部非源实例的跳过选择点。
     *
     * 标记位置直接使用当前实例的半径锚点目标坐标；即使实例已经跳过，白色标记仍保留，
     * 使用户可以再次点击恢复，同时 PropertyManager 列表也保留删除恢复入口。
     *
     * @param sketchGroup 当前活动草图组。
     */
    private syncSkipInstancePreview(sketchGroup: SketchGroup): void {
        const skippedIndexSet = new Set(this.parameters.skippedInstanceIndexes);
        const frames = this.calculateDistributionItems([])
            .filter((item) => !item.isSource)
            .map((item) => ({
                instanceIndex: item.instanceIndex,
                position: sketchGroup.referencePlane.localToWorld(new THREE.Vector2(
                    this.parameters.center2d[0] + Math.cos(item.angleRad) * this.parameters.radius,
                    this.parameters.center2d[1] + Math.sin(item.angleRad) * this.parameters.radius,
                )),
                skipped: skippedIndexSet.has(item.instanceIndex),
            }));
        const preview = this.ensureSkipInstancePreview();
        preview.updateMarkers(frames);
        if (!preview.parent) {
            this.viewer.addObject(preview);
        }
    }

    /**
     * 确保跳过实例预览对象已创建。
     *
     * @returns 可复用预览对象。
     */
    private ensureSkipInstancePreview(): SketchCircularPatternSkipInstancePreview {
        if (this.skipInstancePreview) {
            return this.skipInstancePreview;
        }
        this.skipInstancePreview = new SketchCircularPatternSkipInstancePreview();
        return this.skipInstancePreview;
    }

    /**
     * 清理跳过实例预览对象。
     */
    private clearSkipInstancePreview(): void {
        const preview = this.skipInstancePreview;
        if (!preview) {
            return;
        }
        preview.parent?.remove(preview);
        preview.dispose();
        this.skipInstancePreview = null;
    }

    /**
     * 切换预览实例的跳过状态。
     *
     * @param object 被点选的预览对象或其子对象。
     */
    private toggleSkippedPreviewInstance(object: THREE.Object3D): void {
        const instanceIndex = this.skipInstancePreview?.resolveInstanceIndexByMarker(object) ?? null;
        if (instanceIndex === null || instanceIndex === 0) {
            return;
        }
        const skippedIndexSet = new Set(this.parameters.skippedInstanceIndexes);
        if (skippedIndexSet.has(instanceIndex)) {
            skippedIndexSet.delete(instanceIndex);
        } else {
            skippedIndexSet.add(instanceIndex);
        }
        this.updateParameters({skippedInstanceIndexes: [...skippedIndexSet].sort((left, right) => left - right)});
    }

    /**
     * 计算当前参数对应的实例分布。
     *
     * @param skippedInstanceIndexes 本次计算需要排除的稳定实例索引。
     * @returns 圆周分布结果。
     */
    private calculateDistributionItems(
        skippedInstanceIndexes: readonly number[] = this.parameters.skippedInstanceIndexes,
    ) {
        return this.distributionCalculator.calculate({
            instanceCount: this.parameters.instanceCount,
            startAngleRad: THREE.MathUtils.degToRad(this.parameters.startAngleDeg),
            totalAngleRad: THREE.MathUtils.degToRad(this.parameters.totalAngleDeg),
            equalSpacing: this.parameters.equalSpacing,
            spacingAngleRad: THREE.MathUtils.degToRad(this.parameters.spacingAngleDeg),
            direction: this.parameters.reverseDirection ? 'clockwise' : 'counterclockwise',
            skippedInstanceIndexes,
        });
    }

    /**
     * 创建单个实例从原始源几何到目标位置的刚体变换矩阵。
     *
     * @param targetAngleRad 目标实例极角。
     * @param distributionRotationRad 相对源实例的阵列旋转角。
     * @param sourceAnchorWorld 原始源半径锚点。
     * @returns 实例刚体变换矩阵。
     */
    private createInstanceTransformMatrix(
        targetAngleRad: number,
        distributionRotationRad: number,
        sourceAnchorWorld: THREE.Vector3,
    ): THREE.Matrix4 {
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup) {
            return new THREE.Matrix4();
        }
        const targetLocal = new THREE.Vector2(
            this.parameters.center2d[0] + Math.cos(targetAngleRad) * this.parameters.radius,
            this.parameters.center2d[1] + Math.sin(targetAngleRad) * this.parameters.radius,
        );
        const targetWorld = sketchGroup.referencePlane.localToWorld(targetLocal);
        const startAngleDelta = THREE.MathUtils.degToRad(this.parameters.startAngleDeg) - this.sourceStartAngleRad;
        const rotationRad = this.parameters.rotateInstances
            ? startAngleDelta + distributionRotationRad
            : 0;
        const toSourceOrigin = new THREE.Matrix4().makeTranslation(
            -sourceAnchorWorld.x,
            -sourceAnchorWorld.y,
            -sourceAnchorWorld.z,
        );
        const rotationMatrix = new THREE.Matrix4().makeRotationAxis(
            sketchGroup.referencePlane.normal.clone().normalize(),
            rotationRad,
        );
        const toTarget = new THREE.Matrix4().makeTranslation(targetWorld.x, targetWorld.y, targetWorld.z);
        return toTarget.multiply(rotationMatrix).multiply(toSourceOrigin);
    }

    /**
     * 通过统一参数系统落位源实例。
     *
     * @param targetAngleRad 源实例目标极角。
     * @param distributionRotationRad 源实例分布旋转角，正常情况下为 0。
     * @param sourceAnchorWorld 原始源半径锚点。
     * @throws 当活动关系或 driving 尺寸拒绝目标位置时抛出错误。
     */
    private applySourceInstanceTransform(
        targetAngleRad: number,
        distributionRotationRad: number,
        sourceAnchorWorld: THREE.Vector3,
    ): void {
        const matrix = this.createInstanceTransformMatrix(
            targetAngleRad,
            distributionRotationRad,
            sourceAnchorWorld,
        );
        const targets = new Map<SketchPointObject, THREE.Vector3>();
        this.geometryService.collectUniquePoints(this.selectedEntities).forEach((point) => {
            targets.set(point, point.getPosition().clone().applyMatrix4(matrix));
        });
        if (targets.size === 0 || !this.viewer.previewSketchParametricPointTargets(
            targets,
            'circular-pattern-source-instance',
        )) {
            throw new Error('创建圆周草图阵列失败：源实体目标位置与当前关系或尺寸冲突。');
        }
    }

    /**
     * 创建派生实例相对已求解源实例的刚体变换。
     *
     * @param targetAngleRad 派生实例目标极角。
     * @param distributionRotationRad 相对源实例的分布旋转角。
     * @param sourceAnchorWorld 已求解源实例的半径锚点。
     * @returns 派生实例刚体变换矩阵。
     */
    private createDerivedInstanceTransformMatrix(
        targetAngleRad: number,
        distributionRotationRad: number,
        sourceAnchorWorld: THREE.Vector3,
    ): THREE.Matrix4 {
        const sketchGroup = this.resolveActiveSketchGroup();
        if (!sketchGroup) {
            return new THREE.Matrix4();
        }
        const targetWorld = sketchGroup.referencePlane.localToWorld(new THREE.Vector2(
            this.parameters.center2d[0] + Math.cos(targetAngleRad) * this.parameters.radius,
            this.parameters.center2d[1] + Math.sin(targetAngleRad) * this.parameters.radius,
        ));
        if (!this.parameters.rotateInstances) {
            return new THREE.Matrix4().makeTranslation(
                targetWorld.x - sourceAnchorWorld.x,
                targetWorld.y - sourceAnchorWorld.y,
                targetWorld.z - sourceAnchorWorld.z,
            );
        }
        const toSourceOrigin = new THREE.Matrix4().makeTranslation(
            -sourceAnchorWorld.x,
            -sourceAnchorWorld.y,
            -sourceAnchorWorld.z,
        );
        const rotationMatrix = new THREE.Matrix4().makeRotationAxis(
            sketchGroup.referencePlane.normal.clone().normalize(),
            distributionRotationRad,
        );
        const toTarget = new THREE.Matrix4().makeTranslation(targetWorld.x, targetWorld.y, targetWorld.z);
        return toTarget.multiply(rotationMatrix).multiply(toSourceOrigin);
    }

    /**
     * 将原始选择对象规范化为可阵列草图实体。
     *
     * @param objects 原始对象。
     * @returns 去重后的草图实体。
     */
    private resolveEntities(objects: readonly THREE.Object3D[]): THREE.Object3D[] {
        const sketchGroup = this.resolveActiveSketchGroup();
        return sketchGroup
            ? this.selectionService.resolveTransformEntities(objects, sketchGroup)
            : [];
    }

    /**
     * 获取源实体持久 ID。
     *
     * @returns 与源实体顺序一致的持久 ID。
     */
    private resolveSourcePersistentIds(): string[] {
        return this.selectedEntities.map((entity) => {
            const persistentId = Reflect.get(entity, 'persistentId');
            if (typeof persistentId !== 'string') {
                throw new Error(`圆周草图阵列源实体缺少持久 ID：${entity.uuid}`);
            }
            return persistentId;
        });
    }

    /**
     * 解析活动草图组。
     *
     * @returns 活动草图组；没有活动草图时返回 null。
     */
    private resolveActiveSketchGroup(): SketchGroup | null {
        return this.viewer.sketchGroupManager?.activeSketchGroup ?? null;
    }

    /**
     * 判断当前是否可以生成预览。
     *
     * @returns 参数与源实体均有效时返回 true。
     */
    private canPreview(): boolean {
        return this.selectedEntities.length > 0
            && this.sourceAnchorWorld !== null
            && this.parameters.instanceCount >= MINIMUM_PATTERN_INSTANCE_COUNT
            && this.parameters.radius > PATTERN_GEOMETRY_EPSILON;
    }

    /**
     * 判断当前是否允许确认。
     *
     * @returns 可以确认时返回 true。
     */
    private canConfirm(): boolean {
        return this.active
            && this.canPreview()
            && this.calculateDistributionItems().some((item) => !item.isSource);
    }

    /**
     * 规范化阵列参数。
     *
     * @param parameters 候选参数。
     * @returns 可安全用于预览和确认的参数。
     */
    private normalizeParameters(parameters: SketchCircularPatternParameters): SketchCircularPatternParameters {
        const instanceCount = Number.isFinite(parameters.instanceCount)
            ? Math.max(MINIMUM_PATTERN_INSTANCE_COUNT, Math.floor(parameters.instanceCount))
            : this.parameters.instanceCount;
        const skippedInstanceIndexes = [...new Set(parameters.skippedInstanceIndexes)]
            .filter((index) => Number.isInteger(index) && index > 0 && index < instanceCount)
            .sort((left, right) => left - right);
        return {
            ...parameters,
            center2d: [
                Number.isFinite(parameters.center2d[0]) ? parameters.center2d[0] : this.parameters.center2d[0],
                Number.isFinite(parameters.center2d[1]) ? parameters.center2d[1] : this.parameters.center2d[1],
            ],
            radius: Number.isFinite(parameters.radius)
                ? Math.max(parameters.radius, PATTERN_GEOMETRY_EPSILON)
                : this.parameters.radius,
            startAngleDeg: Number.isFinite(parameters.startAngleDeg)
                ? parameters.startAngleDeg
                : this.parameters.startAngleDeg,
            totalAngleDeg: Number.isFinite(parameters.totalAngleDeg)
                ? Math.max(Math.min(Math.abs(parameters.totalAngleDeg), 360), PATTERN_GEOMETRY_EPSILON)
                : this.parameters.totalAngleDeg,
            spacingAngleDeg: Number.isFinite(parameters.spacingAngleDeg)
                ? Math.max(Math.abs(parameters.spacingAngleDeg), PATTERN_GEOMETRY_EPSILON)
                : this.parameters.spacingAngleDeg,
            instanceCount,
            skippedInstanceIndexes,
        };
    }

    /**
     * 创建默认阵列参数。
     *
     * @returns 默认参数。
     */
    private createDefaultParameters(): SketchCircularPatternParameters {
        return {
            center2d: [0, 0],
            radius: 0.1,
            startAngleDeg: 0,
            totalAngleDeg: 360,
            equalSpacing: true,
            spacingAngleDeg: 90,
            reverseDirection: false,
            instanceCount: 4,
            rotateInstances: true,
            showRadiusDimension: false,
            showSpacingDimension: false,
            showInstanceCount: true,
            skippedInstanceIndexes: [],
        };
    }

    /**
     * 创建控制器快照。
     *
     * @returns 深复制快照。
     */
    private createSnapshot(): SketchCircularPatternControllerSnapshot {
        return {
            active: this.active,
            activeSelectionRole: this.activeSelectionRole,
            selectedItems: this.selectionService.buildSelectedItems(this.selectedEntities),
            centerItem: this.centerItem
                ? {...this.centerItem, position: this.centerItem.position.clone()}
                : null,
            parameters: {
                ...this.parameters,
                center2d: [this.parameters.center2d[0], this.parameters.center2d[1]],
                skippedInstanceIndexes: [...this.parameters.skippedInstanceIndexes],
            },
            canConfirm: this.canConfirm(),
        };
    }

    /**
     * 创建未激活初始快照。
     *
     * @returns 初始快照。
     */
    private createInitialSnapshot(): SketchCircularPatternControllerSnapshot {
        return {
            active: false,
            activeSelectionRole: 'entities',
            selectedItems: [],
            centerItem: null,
            parameters: this.createDefaultParameters(),
            canConfirm: false,
        };
    }

    /**
     * 发布最新控制器快照。
     */
    private notifySnapshot(): void {
        this.snapshotSubject.next(this.createSnapshot());
    }

    /**
     * 提交当前历史会话。
     */
    private commitHistorySession(): void {
        const historySession = this.historySession;
        this.historySession = null;
        const commitResult = historySession?.commit();
        if (commitResult && typeof (commitResult as PromiseLike<void>).then === 'function') {
            void (commitResult as PromiseLike<void>).then(undefined, (error) => {
                (globalThis as {reportError?: (reportedError: unknown) => void}).reportError?.(error);
            });
        }
    }

    /**
     * 取消当前历史会话。
     */
    private cancelHistorySession(): void {
        const historySession = this.historySession;
        this.historySession = null;
        historySession?.cancel();
    }
}
